// DownloadPDF.jsx
import React, { useEffect, useRef } from "react";
import jsPDF from "jspdf";
import { calculatorNotes } from "./CalculatorNotes";
import backgroundImg from "../../../Assets/Pdf/pdf_bg.jpeg";

export const DownloadPDF = ({
  result,
  checklist,
  score,
  phone,
  calculatorName,
  clearForm,
  autoDownload = false,
  onAutoDownloadComplete,
}) => {
  const hasAutoDownloaded = useRef(false);

  const downloadPDF = async () => {
    const doc = new jsPDF();

    // --- Load background image ---
    const bgImage = new Image();
    bgImage.src = backgroundImg;
    await new Promise((resolve) => {
      bgImage.onload = resolve;
    });

    // Helper to add background to current page
    const addBackground = () => {
      doc.addImage(
        bgImage,
        "JPEG",
        0,
        0,
        doc.internal.pageSize.width,
        doc.internal.pageSize.height,
      );
    };

    addBackground();

    // --- Content (starting lower to avoid header) ---
    let y = 47;

    doc.setFontSize(16);
    doc.text(calculatorName, 105, y, { align: "center" });
    y += 8;

    doc.setFontSize(12);
    doc.text("Concrete Estimate Report", 105, y, { align: "center" });
    y += 7;

    doc.setFontSize(10);
    doc.text(`Generated : ${new Date().toLocaleString()}`, 15, y);
    y += 10;

    // --- Estimation & Readiness Status in grid (two columns) ---
    const leftX = 15;
    const rightX = 105; // approx half of page width

    // Headings
    doc.setFontSize(12);
    doc.text("Estimation", leftX, y);
    doc.text("Readiness Status", rightX, y);
    y += 7;

    // Details
    doc.setFontSize(11);
    // Left column: Estimation
    doc.text(`Volume Required : ${result.volumeM3.toFixed(2)} m³`, leftX, y);
    y += 7;
    doc.text(`Weight : ${result.weightKg.toFixed(2)} kg`, leftX, y);
    // Right column: Readiness Status (same y as first line of estimation)
    let status = "Ready";
    if (score < 70) status = "High Risk";
    else if (score < 90) status = "Minor Risks";

    const yRight = y - 7; // because we advanced y by 7 twice
    doc.text(`Score : ${score}/100`, rightX, yRight);
    doc.text(`Status : ${status}`, rightX, yRight + 5);

    // Move y past both columns (using the left column height)
    y += 12; // extra spacing after grid

    // --- Completed Checklist (font size reduced) ---
    doc.setFontSize(12);
    doc.text("Completed Checklist", 15, y);
    y += 7;

    doc.setFontSize(10); // reduced from 11
    checklist
      .filter((item) => item.checked)
      .forEach((item) => {
        doc.text(`• ${item.label}`, 20, y);
        y += 6; // reduced line height to fit more items
        if (y > 270) {
          doc.addPage();
          addBackground();
          y = 45;
        }
      });
    y += 7;

    // --- Important Notes (unchanged) ---
    doc.setFontSize(12);
    doc.text("Important Notes", 15, y);
    y += 7;

    doc.setFontSize(10);
    calculatorNotes.forEach((note) => {
      const lines = doc.splitTextToSize(`• ${note}`, 170);
      if (y + lines.length * 6 > 280) {
        doc.addPage();
        addBackground();
        y = 45;
      }
      doc.text(lines, 20, y);
      y += lines.length * 6 + 2;
    });
    y += 7;

    // --- Enquiry (unchanged) ---
    doc.setFontSize(12);
    doc.text("For Enquiry", 15, y);
    y += 7;
    doc.setFontSize(9);
    doc.text(phone, 20, y);

    // --- Save PDF ---
    doc.save(`${calculatorName.replace(/[\/\\]/g, "-")}.pdf`);

    if (clearForm) {
      clearForm();
    }
  };

  // --- Auto‑download effect (unchanged) ---
  useEffect(() => {
    if (autoDownload && !hasAutoDownloaded.current) {
      hasAutoDownloaded.current = true;
      downloadPDF().then(() => {
        if (onAutoDownloadComplete) {
          onAutoDownloadComplete();
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDownload, onAutoDownloadComplete]);

  // --- Manual download button (unchanged) ---
  return (
    <button
      onClick={downloadPDF}
      className="px-11 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
    >
      📄 Download Concrete PDF
    </button>
  );
};

export default DownloadPDF;
